import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import useToast from '../../../hooks/useToast';

const NotificationsTab = ({ onSave, organizationId }) => {
  const [emailNotifications, setEmailNotifications] = useState({
    projectUpdates: true,
    taskAssignments: true,
    mentions: true,
    dueDates: true,
    comments: false,
    weeklyDigest: true,
    securityAlerts: true
  });

  const [inAppNotifications, setInAppNotifications] = useState({
    projectUpdates: true,
    taskAssignments: true,
    mentions: true,
    dueDates: true,
    comments: true,
    teamActivity: false
  });

  const [projectSpecific, setProjectSpecific] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Load real projects and saved preferences
  useEffect(() => {
    const loadData = async () => {
      if (!organizationId) return;
      try {
        setLoadingProjects(true);
        setLoadError('');
        
        // Import services
        const { default: apiService } = await import('../../../utils/apiService.js');
        const { default: realApiService } = await import('../../../utils/realApiService.js');
        
        // Fetch projects and preferences in parallel
        const [projectsResult, preferencesResult] = await Promise.all([
          apiService.projects.getAll(organizationId),
          realApiService.notifications.getPreferences()
        ]);

        console.log('🔄 Loaded preferences:', preferencesResult);

        // Update Global Settings (Email, InApp, QuietHours) from saved preferences
        if (preferencesResult && preferencesResult.success !== false) {
           const prefs = preferencesResult.data || preferencesResult; // Handle various response shapes
           
           if (prefs.email) setEmailNotifications(prev => ({ ...prev, ...prefs.email }));
           if (prefs.inApp) setInAppNotifications(prev => ({ ...prev, ...prefs.inApp }));
           // Projects handled below
        }

        // Process Projects
        const map = {};
        // projects may be array or wrapped, normalize
        const list = Array.isArray(projectsResult?.projects) ? projectsResult.projects : Array.isArray(projectsResult) ? projectsResult : (projectsResult?.data || []);
        
        // Get saved project preferences
        const savedProjectPrefs = (preferencesResult?.data || preferencesResult)?.projects || {};

        list.forEach(p => {
          const id = p.id || p.project_id || p._id;
          if (!id) return;
          
          // Default settings
          const defaults = {
            name: p.name || 'Project',
            enabled: true,
            mentions: true,
            updates: true,
            deadlines: true,
          };

          // Merge with saved settings if they exist
          map[id] = {
            ...defaults,
            ...(savedProjectPrefs[id] || {})
          };
        });
        setProjectSpecific(map);
      } catch (e) {
        console.error('Failed to load settings:', e);
        setLoadError(e.message || 'Failed to load settings');
      } finally {
        setLoadingProjects(false);
      }
    };
    loadData();
  }, [organizationId]);


  const handleEmailNotificationChange = (key, checked) => {
    setEmailNotifications(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleInAppNotificationChange = (key, checked) => {
    setInAppNotifications(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleProjectNotificationChange = (projectId, key, checked) => {
    setProjectSpecific(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        [key]: checked
      }
    }));
  };


  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  const handleSave = async () => {
    try {
      setSaving(true);
      const notificationSettings = {
        email: emailNotifications,
        inApp: inAppNotifications,
        projects: projectSpecific
      };
      await onSave(notificationSettings);
      showToast('Notification preferences saved successfully!', 'success');
    } catch (error) {
      console.error('Failed to save notifications:', error);
      showToast('Failed to save preferences. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = () => {
    // Mock test notification
    console.log('Sending test notification...');
  };

  return (
    <div className="space-y-8">
      {/* Email Notifications */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={20} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-foreground">Email Notifications</h3>
              <p className="text-sm text-muted-foreground">
                Choose what updates you want to receive via email
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestNotification}
            iconName="Send"
            iconPosition="left"
          >
            Test Email
          </Button>
        </div>

        <div className="space-y-4">
          <Checkbox
            label="Project Updates"
            description="Get notified when projects you're involved in have updates"
            checked={emailNotifications.projectUpdates}
            onChange={(e) => handleEmailNotificationChange('projectUpdates', e.target.checked)}
          />
          
          <Checkbox
            label="Task Assignments"
            description="Receive emails when you're assigned to new tasks"
            checked={emailNotifications.taskAssignments}
            onChange={(e) => handleEmailNotificationChange('taskAssignments', e.target.checked)}
          />
          
          <Checkbox
            label="Mentions"
            description="Get notified when someone mentions you in comments"
            checked={emailNotifications.mentions}
            onChange={(e) => handleEmailNotificationChange('mentions', e.target.checked)}
          />
          
          <Checkbox
            label="Due Dates"
            description="Reminders for upcoming task deadlines"
            checked={emailNotifications.dueDates}
            onChange={(e) => handleEmailNotificationChange('dueDates', e.target.checked)}
          />
          
          <Checkbox
            label="Comments"
            description="New comments on tasks you're watching"
            checked={emailNotifications.comments}
            onChange={(e) => handleEmailNotificationChange('comments', e.target.checked)}
          />
          
          <Checkbox
            label="Weekly Digest"
            description="Summary of your week's activity and upcoming tasks"
            checked={emailNotifications.weeklyDigest}
            onChange={(e) => handleEmailNotificationChange('weeklyDigest', e.target.checked)}
          />
          
          <Checkbox
            label="Security Alerts"
            description="Important security notifications (always enabled)"
            checked={emailNotifications.securityAlerts}
            onChange={(e) => handleEmailNotificationChange('securityAlerts', e.target.checked)}
            disabled
          />
        </div>
      </div>

      {/* In-App Notifications */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Bell" size={20} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">In-App Notifications</h3>
            <p className="text-sm text-muted-foreground">
              Control what notifications appear in the application
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Checkbox
            label="Project Updates"
            description="Show notifications for project changes"
            checked={inAppNotifications.projectUpdates}
            onChange={(e) => handleInAppNotificationChange('projectUpdates', e.target.checked)}
          />
          
          <Checkbox
            label="Task Assignments"
            description="Notify when you're assigned to tasks"
            checked={inAppNotifications.taskAssignments}
            onChange={(e) => handleInAppNotificationChange('taskAssignments', e.target.checked)}
          />
          
          <Checkbox
            label="Mentions"
            description="Show when you're mentioned in comments"
            checked={inAppNotifications.mentions}
            onChange={(e) => handleInAppNotificationChange('mentions', e.target.checked)}
          />
          
          <Checkbox
            label="Due Dates"
            description="Reminders for task deadlines"
            checked={inAppNotifications.dueDates}
            onChange={(e) => handleInAppNotificationChange('dueDates', e.target.checked)}
          />
          
          <Checkbox
            label="Comments"
            description="New comments on your tasks"
            checked={inAppNotifications.comments}
            onChange={(e) => handleInAppNotificationChange('comments', e.target.checked)}
          />
          
          <Checkbox
            label="Team Activity"
            description="General team activity updates"
            checked={inAppNotifications.teamActivity}
            onChange={(e) => handleInAppNotificationChange('teamActivity', e.target.checked)}
          />
        </div>
      </div>

      {/* Project-Specific Notifications */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="FolderOpen" size={20} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Project-Specific Settings</h3>
            <p className="text-sm text-muted-foreground">
              Customize notifications for individual projects
            </p>
          </div>
        </div>

        {loadError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{loadError}</div>
        )}

        {loadingProjects && (
          <div className="text-sm text-muted-foreground">Loading projects...</div>
        )}

        <div className="space-y-6">
          {Object.entries(projectSpecific).map(([projectId, project]) => (
            <div key={projectId} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Folder" size={16} className="text-primary-foreground" />
                  </div>
                  <h4 className="font-medium text-foreground">{project.name}</h4>
                </div>
                <Checkbox
                  label="Enable notifications"
                  checked={project.enabled}
                  onChange={(e) => handleProjectNotificationChange(projectId, 'enabled', e.target.checked)}
                />
              </div>

              {project.enabled && (
                <div className="ml-11 space-y-3">
                  <Checkbox
                    label="Mentions"
                    checked={project.mentions}
                    onChange={(e) => handleProjectNotificationChange(projectId, 'mentions', e.target.checked)}
                    size="sm"
                  />
                  <Checkbox
                    label="Project Updates"
                    checked={project.updates}
                    onChange={(e) => handleProjectNotificationChange(projectId, 'updates', e.target.checked)}
                    size="sm"
                  />
                  <Checkbox
                    label="Deadlines"
                    checked={project.deadlines}
                    onChange={(e) => handleProjectNotificationChange(projectId, 'deadlines', e.target.checked)}
                    size="sm"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Sticky Action Bar */}
      <div className="sticky bottom-0 left-0 right-0 bg-card/80 backdrop-blur-md border-t border-border p-4 -mx-6 -mb-8 mt-8 flex items-center justify-between shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10 rounded-b-lg">
        <p className="text-sm text-muted-foreground hidden md:block">
          Changes will be applied across all devices
        </p>
        <Button
          variant="default"
          size="lg"
          onClick={handleSave}
          loading={saving}
          iconName="Save"
          iconPosition="left"
          disabled={saving}
          className="px-8 shadow-md hover:shadow-lg transition-all duration-200 w-full md:w-auto"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
};

export default NotificationsTab;